import React, { useEffect, useState } from 'react';
import _, { assign } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlusIcon from '@mui/icons-material/Add';
import { CircularProgress, Alert, Grid, Button, TextField, Typography, Divider, Paper, Box, IconButton } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import i18next, { t, use } from 'i18next';
import { useWatch } from 'react-hook-form';
import { useApolloClient, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import PagedTable from 'components/table/PagedTable';
import SimpleTable from 'components/table/SimpleTable';
import SortableTaskHeader from 'components/table/SortableTableHeader';
import {
  EOfferSource,
  EOfferStatus,
  EOfferVersionStatus,
  EOfferVersionType,
  ETM_TaskPriority,
  ETM_TaskStatus,
  TM_TaskOutput,
} from '__generated__/graphql';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import SimpleAccordion from 'components/SimpleAccordion';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { FormatDateTime, FormatDateRange, FormatDate, FormatDateTimeShort } from 'components/DateTime';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/ForwardToInbox';
import { filterSelector } from 'helper/filter';
import { RedirectError } from 'pages/error';
import {
  CREATE_TASK,
  LIST_TASK_CATEGORIES,
  LIST_TASK_SUBJECTS,
  LIST_TASK_USERS,
  LIST_TASKS,
  UPDATE_TASK,
  UPDATE_TASK_FILTER,
  VIEW_TASK_FILTER,
  LIST_TASK_CATEGORY_GROUPS,
  ADD_TASK_COMMENT,
  UPDATE_TASK_COMMENT,
  DELETE_TASK_COMMENT,
  LIST_TASK_HOTELS_PAGED,
  LIST_TASK_PUBLIC_COMPANIES_PAGED,
  LIST_TASK_PUBLIC_CLIENTS_PAGED,
  LIST_TASK_USERS_PAGED,
  LIST_TASK_BOOKINGS_PAGED,
  GET_BOOKING_FOR_TASK,
} from 'pages/tasks/gql';
import SimpleDialog from 'components/dialogs/Dialog';
import yup from 'validation';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputDropdown, SimpleDropdown } from 'components/form/FormInputDropdown';
import { FormInputLazyAutocomplete } from 'components/form/FormInputLazyAutocomplete';
import {
  ListTaskHotelsPagedQuery,
  ListTaskPublicCompaniesPagedQuery,
  ListTaskPublicClientsPagedQuery,
  ListTaskUsersPagedQuery,
  ListTaskBookingsPagedQuery,
} from '__generated__/graphql';
import { Check, Edit, Cancel, FilterAlt, Settings, Star, Save } from '@mui/icons-material';
import { FormInputMultiCheckbox } from 'components/form/FormInputMultiCheckbox';
import { time } from 'console';
import { userSelector } from 'helper/security';
import moment from 'moment';
import { toggleSort, TaskSortColumn } from 'store/taskSortSlice';
import type { RootState } from 'store';
import { ETM_TaskSortColumn } from '__generated__/graphql';

const validationSchema = yup.object().shape({
  description: yup.string(),
  dueDate: yup.date().required(),
  status: yup.string().required(),
  categoryId: yup.number().required(),
  subjectId: yup.number().required(),
  assigneeId: yup.number().nullable(),
  hotelId: yup.number().nullable(),
  offerId: yup.number().nullable(),
  publicClientId: yup.number().nullable(),
  publicCompanyId: yup.number().nullable(),
});

const validationSchemaFilter = yup.object().shape({
  status: yup.mixed().required(),
  assigneeId: yup.number().nullable(),
  categoryIds: yup.array().of(yup.number()),
  hotelId: yup.number().nullable(),
  publicClientId: yup.number().nullable(),
  publicCompanyId: yup.number().nullable(),
  dueFrom: yup.date().nullable(),
  dueTo: yup.date().nullable(),
});

const validationSchemaComment = yup.object().shape({
  comment: yup.string().required(),
});

type TaskFormType = yup.InferType<typeof validationSchema>;
type TaskFilterFormType = yup.InferType<typeof validationSchemaFilter>;
type TaskCommentFormType = yup.InferType<typeof validationSchemaComment>;

interface TasksListProps {
  mode: 'full' | 'booking' | 'company' | 'contact' | 'hotel';
  bookingId?: number;
  bookingRefCode?: string;
  bookingTitle?: string;
  publicClientId?: number;
  publicCompanyId?: number;
  hotelId?: number;
  hideFilters?: boolean;
  hideSettings?: boolean;
  hideCreateButton?: boolean;
  showHotelColumn?: boolean;
}

/**
 * Main tasks list component supporting multiple modes (full, booking).
 * Handles task filtering, sorting, creation, updates, and comments.
 * Supports pagination and lazy loading of related entities (hotels, clients, companies).
 */
export default function TasksList(props: TasksListProps) {
  const { mode, bookingId, publicClientId, publicCompanyId, hotelId, hideFilters = false, hideSettings = false, hideCreateButton = false, showHotelColumn = false } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = filterSelector();
  const { sortColumn, sortDirection } = useSelector((state: RootState) => state.taskSort);

  const handleSort = (column: TaskSortColumn) => {
    dispatch(toggleSort(column));
  };

  const [showTaskDialog, setShowTaskDialog] = React.useState(false);
  const [showFilterDialog, setShowFilterDialog] = React.useState(false);
  const [taskToEdit, setTaskToEdit] = React.useState<TM_TaskOutput | null>(null);
  const [editingCommentId, setEditingCommentId] = React.useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = React.useState<string>('');

  const [quickfilterAssignee, setQuickfilterAssignee] = React.useState<string | null | number>(null);
  const [filterVersion, setFilterVersion] = React.useState(0);
  const [appliedFilter, setAppliedFilter] = React.useState<{
    status: ETM_TaskStatus;
    dueFrom?: Date | null;
    dueTo?: Date | null;
    assigneeId?: number | null;
    categoryIds?: number[];
  }>({ status: ETM_TaskStatus.OPEN });

  const [createTask] = useMutation(CREATE_TASK);
  const [updateTask] = useMutation(UPDATE_TASK);
  const [updateTaskFilter] = useMutation(UPDATE_TASK_FILTER);
  const [addTaskComment] = useMutation(ADD_TASK_COMMENT);
  const [updateTaskComment] = useMutation(UPDATE_TASK_COMMENT);
  const [deleteTaskComment] = useMutation(DELETE_TASK_COMMENT);

  const { data, loading, error, refetch } = useQuery(LIST_TASKS, {
    variables: {
      ...(bookingId ? { bookingId } : {}),
      ...(mode === 'company' && publicCompanyId ? { publicCompanyId } : {}),
      ...(mode === 'contact' && publicClientId ? { publicClientId } : {}),
      ...(mode === 'hotel' && hotelId ? { hotelId } : {}),
      // Entity-scoped views bypass the saved DB filter entirely by passing useExplicitFilter=true
      // and supplying all filter dimensions explicitly (null/empty = no filter for that dimension)
      ...(mode !== 'full' ? {
        useExplicitFilter: true,
        status: appliedFilter.status,
        dueFrom: appliedFilter.dueFrom ?? null,
        dueTo: appliedFilter.dueTo ?? null,
        assigneeId: appliedFilter.assigneeId ?? null,
        categoryIds: appliedFilter.categoryIds ?? [],
      } : {}),
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    },
    fetchPolicy: 'network-only',
  });

  const { data: taskCategoriesData, loading: taskCategoriesLoading, error: taskCategoriesError } = useQuery(LIST_TASK_CATEGORIES);
  const { data: taskCategoryGroupsData, loading: taskCategoryGroupsLoading, error: taskCategoryGroupsError } = useQuery(LIST_TASK_CATEGORY_GROUPS);
  const { data: taskSubjectsData, loading: taskSubjectsLoading, error: taskSubjectsError } = useQuery(LIST_TASK_SUBJECTS);
  const { data: taskUsersData } = useQuery(LIST_TASK_USERS);
  const { data: taskFilterData, loading: taskFilterLoading, error: taskFilterError } = useQuery(VIEW_TASK_FILTER);

  const _createDefaultValues = {
    description: '',
    dueDate: new Date(),
    status: ETM_TaskStatus.OPEN,
    categoryId: null,
    subjectId: null,
    assigneeId: null,
    hotelId: mode === 'hotel' ? hotelId ?? null : null,
    bookingId: mode === 'booking' ? bookingId ?? null : null,
    publicClientId: (mode === 'booking' || mode === 'contact') ? publicClientId ?? null : null,
    publicCompanyId: (mode === 'booking' || mode === 'company') ? publicCompanyId ?? null : null,
  };;

  const user = userSelector();

  const [getBooking, { data: bookingData }] = useLazyQuery(GET_BOOKING_FOR_TASK);
  const [bookingHotels, setBookingHotels] = useState<Array<{ id: number; name: string }>>([]);
  const [clientCompanyName, setClientCompanyName] = useState<string | null>(null);
  const clientOptionsRef = React.useRef<Array<{ id: number; label: string; companyId: number | null; companyName: string | null }>>([]);


  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    setValue,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: _createDefaultValues,
  });

  const {
    handleSubmit: handleSubmitFilter,
    control: controlFilter,
    trigger: triggerFilter,
    reset: resetFilter,
    watch: watchFilter,
    setValue: setValueFilter,
    formState: { errors: validationErrorsFilter, isDirty: isDirtyFilter, isValidating: isValidatingFilter, isSubmitting: isSubmittingFilter },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchemaFilter),
    context: { client: useApolloClient() },
    defaultValues: { status: ETM_TaskStatus.OPEN, assigneeId: null, categoryIds: [], hotelId: null, publicClientId: null, publicCompanyId: null },
  });

  const {
    handleSubmit: handleSubmitComment,
    control: controlComment,
    trigger: triggerComment,
    reset: resetComment,
    watch: watchComment,
    setValue: setValueComment,
    formState: { errors: validationErrorsComment, isDirty: isDirtyComment, isValidating: isValidatingComment, isSubmitting: isSubmittingComment },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchemaComment),
    context: { client: useApolloClient() },
    defaultValues: { comment: '' },
  });


  useEffect(() => {
    if (watch('categoryId') === null) {
      setValue('subjectId', null);
    }
    if (taskSubjectsData?.listTaskSubjects.find(s => s.id === watch('subjectId'))?.categoryId !== watch('categoryId')) {
      setValue('subjectId', null);
    }
  }, [watch('categoryId')]);

  useEffect(() => {
    const updateFilter = async (): Promise<void> => {
      if (taskFilterData) {
        const assigneeId = (taskFilterData?.viewTaskFilter?.assignees || []).length > 0 ? (taskFilterData?.viewTaskFilter?.assignees || [])[0].id : null;

        if (mode === 'full') {
          // Main Tasks page: initialise filter dialog from saved DB filter
          resetFilter({
            status: taskFilterData?.viewTaskFilter?.status || ETM_TaskStatus.OPEN,
            assigneeId: assigneeId,
            categoryIds: taskFilterData?.viewTaskFilter?.categories.map(c => c.id),
            hotelId: taskFilterData?.viewTaskFilter?.hotels?.[0]?.id ?? null,
            publicClientId: null,
            publicCompanyId: null,
            dueFrom: taskFilterData?.viewTaskFilter?.dueFrom,
            dueTo: taskFilterData?.viewTaskFilter?.dueTo,
          });
        } else {
          // Entity-scoped views: start with clean defaults so the saved Tasks-page filter never bleeds in
          resetFilter({
            status: ETM_TaskStatus.OPEN,
            assigneeId: null,
            categoryIds: [],
            hotelId: null,
            publicClientId: null,
            publicCompanyId: null,
            dueFrom: null,
            dueTo: null,
          });
        }

        // Only persist filter back to DB from the main Tasks page; entity-scoped views use local state
        if (mode === 'full') {
          await updateTaskFilter({
            variables: {
              input: {
                status: taskFilterData?.viewTaskFilter?.status || ETM_TaskStatus.OPEN,
                assigneeIds: assigneeId ? [assigneeId] : [],
                categoryIds: taskFilterData?.viewTaskFilter?.categories.map(c => c.id) || [],
                hotelIds: taskFilterData?.viewTaskFilter?.hotels?.map(h => h.id) ?? [],
                publicClientIds: [],
                publicCompanyIds: [],
                dueFrom: taskFilterData?.viewTaskFilter?.dueFrom,
                dueTo: taskFilterData?.viewTaskFilter?.dueTo,
              },
            },
          });
        }

        await refetch();
      }
    };

    updateFilter();
  }, [taskFilterData, mode, hotelId, publicClientId, publicCompanyId, resetFilter, updateTaskFilter, refetch]);
  const selectedBookingId = useWatch({ control, name: 'bookingId' });

  useEffect(() => {
    if (selectedBookingId) {
      getBooking({ variables: { id: selectedBookingId } });
    } else {
      setBookingHotels([]);
    }
  }, [selectedBookingId, getBooking]);

  useEffect(() => {
    if (bookingData?.viewBooking) {
      const booking = bookingData.viewBooking;

      if (booking.publicCompanyId && booking.publicCompany) {
        setValue('publicCompanyId', booking.publicCompanyId);
        setClientCompanyName(booking.publicCompany.name || null);
      }

      if (booking.publicClientId) {
        setValue('publicClientId', booking.publicClientId);
      }

      if (booking.offers && booking.offers.length > 0) {
        const hotels = booking.offers
          .map(offer => offer.hotel)
          .filter((hotel): hotel is { id: number; name: string } =>
            hotel !== null && hotel !== undefined && hotel.id !== undefined && hotel.name !== undefined
          );

        const uniqueHotels = Array.from(
          new Map(hotels.map(h => [h.id, h])).values()
        );

        setBookingHotels(uniqueHotels);

      } else {
        setBookingHotels([]);
      }
    }
  }, [bookingData, setValue]);


  const _renderFilterDialog = () => {
    return (
      <SimpleDialog
        maxWidth="md"
        title={String(i18next.t('tasks-filter-title'))}
        open={showFilterDialog}
        setOpen={setShowFilterDialog}
        customActions={
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ justifyContent: 'end' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowFilterDialog(false);
                }}
              >
                {i18next.t('cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ marginLeft: 2 }}
                onClick={async () => {
                  try {
                    if (mode === 'full') {
                      // Main Tasks page: persist filter to DB
                      await updateTaskFilter({
                        variables: {
                          input: {
                            status: watchFilter('status') as ETM_TaskStatus,
                            assigneeIds: !_.isNil(watchFilter('assigneeId')) ? [watchFilter('assigneeId') as number] : [],
                            assigneeEmpty: false,
                            categoryIds: _.isArray(watchFilter('categoryIds')) ? (watchFilter('categoryIds') as number[]) : [],
                            hotelIds: !_.isNil(watchFilter('hotelId')) ? [watchFilter('hotelId') as number] : [],
                            publicClientIds: !_.isNil(watchFilter('publicClientId')) ? [watchFilter('publicClientId') as number] : [],
                            publicCompanyIds: !_.isNil(watchFilter('publicCompanyId')) ? [watchFilter('publicCompanyId') as number] : [],
                            dueFrom: watchFilter('dueFrom'),
                            dueTo: watchFilter('dueTo'),
                          },
                        },
                        refetchQueries: [{ query: VIEW_TASK_FILTER }],
                      });
                    } else {
                      // Entity-scoped views: keep filter local, pass as query variables
                      setAppliedFilter({
                        status: watchFilter('status') as ETM_TaskStatus,
                        dueFrom: watchFilter('dueFrom') as Date | null | undefined,
                        dueTo: watchFilter('dueTo') as Date | null | undefined,
                        assigneeId: watchFilter('assigneeId') as number | null | undefined,
                        categoryIds: _.isArray(watchFilter('categoryIds')) ? (watchFilter('categoryIds') as number[]) : [],
                      });
                    }
                    setFilterVersion(v => v + 1);
                    dispatchMessage(dispatch, String(i18next.t('tasks-filter-saved')));
                    setShowFilterDialog(false);
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('tasks-filter-apply')}
              </Button>
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormInputDropdown
              control={controlFilter}
              label={String(i18next.t('tasks-field-status'))}
              name="status"
              options={[
                { value: ETM_TaskStatus.OPEN, label: String(i18next.t('tasks-status-open')) },
                { value: ETM_TaskStatus.DONE, label: String(i18next.t('tasks-status-done')) },
              ]}
            />
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <FormInputDate timePicker control={controlFilter} label={String(i18next.t('tasks-field-due-from'))} name="dueFrom" />
          </Grid>
          <Grid item xs={6}>
            <FormInputDate timePicker control={controlFilter} label={String(i18next.t('tasks-field-due-to'))} name="dueTo" />
          </Grid>
          <Grid item xs={6}>
            <FormInputLazyAutocomplete<ListTaskUsersPagedQuery, { search: string | null; skip: number; take: number }>
              control={controlFilter}
              label={String(i18next.t('tasks-field-assignee'))}
              name="assigneeId"
              query={LIST_TASK_USERS_PAGED}
              getOptions={data => (data?.listTaskUsersPaged?.users || []).map(user => ({ id: user.id, label: user.name }))}
              getTotalCount={data => data?.listTaskUsersPaged?.totalCount || 0}
              emptyLabel={String(i18next.t('tasks-empty-all'))}
            />
          </Grid>
          <Grid item xs={6}>
            {mode === 'hotel' ? (
              <FormInputLazyAutocomplete<ListTaskHotelsPagedQuery, { search: string | null; skip: number; take: number }>
                control={controlFilter}
                label={String(i18next.t('hotel'))}
                name="hotelId"
                query={LIST_TASK_HOTELS_PAGED}
                getOptions={data => (data?.listTaskHotelsPaged?.hotels || []).map(hotel => ({ id: hotel.id, label: hotel.name }))}
                getTotalCount={data => data?.listTaskHotelsPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
                disabled
              />
            ) : (
              <FormInputLazyAutocomplete<ListTaskHotelsPagedQuery, { search: string | null; skip: number; take: number }>
                control={controlFilter}
                label={String(i18next.t('hotel'))}
                name="hotelId"
                query={LIST_TASK_HOTELS_PAGED}
                getOptions={data => (data?.listTaskHotelsPaged?.hotels || []).map(hotel => ({ id: hotel.id, label: hotel.name }))}
                getTotalCount={data => data?.listTaskHotelsPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {mode === 'company' || mode === 'contact' ? (
              <FormInputLazyAutocomplete<ListTaskPublicCompaniesPagedQuery, { search: string | null; skip: number; take: number }>
                control={controlFilter}
                label={String(i18next.t('company'))}
                name="publicCompanyId"
                query={LIST_TASK_PUBLIC_COMPANIES_PAGED}
                getOptions={data =>
                  (data?.listTaskPublicCompaniesPaged?.companies || []).map(company => ({ id: company.id, label: company.name || '' }))
                }
                getTotalCount={data => data?.listTaskPublicCompaniesPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
                onValueChange={() => setValueFilter('publicClientId', null)}
                disabled
              />
            ) : (
              <FormInputLazyAutocomplete<ListTaskPublicCompaniesPagedQuery, { search: string | null; skip: number; take: number }>
                control={controlFilter}
                label={String(i18next.t('company'))}
                name="publicCompanyId"
                query={LIST_TASK_PUBLIC_COMPANIES_PAGED}
                getOptions={data =>
                  (data?.listTaskPublicCompaniesPaged?.companies || []).map(company => ({ id: company.id, label: company.name || '' }))
                }
                getTotalCount={data => data?.listTaskPublicCompaniesPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
                onValueChange={() => setValueFilter('publicClientId', null)}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {mode === 'contact' ? (
              <FormInputLazyAutocomplete<
                ListTaskPublicClientsPagedQuery,
                { search: string | null; companyId: number | null; skip: number; take: number }
              >
                control={controlFilter}
                label={String(i18next.t('contact-person'))}
                name="publicClientId"
                query={LIST_TASK_PUBLIC_CLIENTS_PAGED}
                getOptions={data =>
                  (data?.listTaskPublicClientsPaged?.clients || []).map(client => ({
                    id: client.id,
                    label: client.firstname + ' ' + client.lastname,
                  }))
                }
                getTotalCount={data => data?.listTaskPublicClientsPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
                additionalVariables={{ companyId: publicCompanyId ?? null }}
                disabled
              />
            ) : (
              <FormInputLazyAutocomplete<
                ListTaskPublicClientsPagedQuery,
                { search: string | null; companyId: number | null; skip: number; take: number }
              >
                control={controlFilter}
                label={String(i18next.t('contact-person'))}
                name="publicClientId"
                query={LIST_TASK_PUBLIC_CLIENTS_PAGED}
                getOptions={data =>
                  (data?.listTaskPublicClientsPaged?.clients || []).map(client => ({
                    id: client.id,
                    label: client.firstname + ' ' + client.lastname,
                  }))
                }
                getTotalCount={data => data?.listTaskPublicClientsPaged?.totalCount || 0}
                emptyLabel={String(i18next.t('tasks-empty-all'))}
                disabled={mode !== 'company' && _.isNil(watchFilter('publicCompanyId'))}
                additionalVariables={{ companyId: mode === 'company' ? publicCompanyId ?? null : watchFilter('publicCompanyId') }}
              />
            )}
          </Grid>
          {(taskCategoryGroupsData?.listTaskCategoryGroups || []).map(categoryGroup => (
            <Grid item xs={6} key={categoryGroup.id}>
              <FormInputMultiCheckbox
                control={controlFilter}
                label={categoryGroup.name}
                name="categoryIds"
                options={(taskCategoriesData?.listTaskCategories || [])
                  .filter(c => c.groupId === categoryGroup.id)
                  .map(c => ({ value: c.id, label: c.name }))}
              />
            </Grid>
          ))}
        </Grid>
      </SimpleDialog>
    );
  };

  const _renderTaskDialog = () => {
    return (
      <SimpleDialog
        maxWidth="md"
        title={taskToEdit ? String(i18next.t('tasks-edit-title')) : String(i18next.t('tasks-create-title'))}
        open={showTaskDialog}
        setOpen={setShowTaskDialog}
        customActions={
          <Grid container spacing={2}>
            <Grid item xs={6} sx={{ justifyContent: 'end' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowTaskDialog(false);
                  setEditingCommentId(null);
                  setEditingCommentText('');
                }}
              >
                {i18next.t('cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ marginLeft: 2 }}
                onClick={async () => {
                  const isValid = await trigger();
                  if (!isValid) {
                    return;
                  }
                  try {
                    if (taskToEdit) {
                      await updateTask({
                        variables: {
                          id: taskToEdit.id,
                          input: {
                            description: watch('description'),
                            dueDate: watch('dueDate'),
                            status: watch('status'),
                            categoryId: watch('categoryId') || 0,
                            subjectId: watch('subjectId') || 0,
                            priority: ETM_TaskPriority.NORMAL,
                            assigneeId: watch('assigneeId') || null,
                            hotelId: watch('hotelId') || null,
                            bookingId: watch('bookingId') || null,
                            publicClientId: watch('publicClientId') || null,
                            publicCompanyId: watch('publicCompanyId') || null,
                          },
                        },
                        refetchQueries: [{ query: LIST_TASKS, variables: { ...(bookingId ? { bookingId } : {}), sortColumn, sortDirection } }],
                      });
                    } else {
                      await createTask({
                        variables: {
                          input: {
                            description: watch('description'),
                            dueDate: watch('dueDate'),
                            status: watch('status'),
                            categoryId: watch('categoryId') || 0,
                            subjectId: watch('subjectId') || 0,
                            priority: ETM_TaskPriority.NORMAL,
                            assigneeId: watch('assigneeId') || null,
                            hotelId: watch('hotelId') || null,
                            bookingId: watch('bookingId') || null,
                            publicClientId: watch('publicClientId') || null,
                            publicCompanyId: watch('publicCompanyId') || null,
                          },
                        },
                        refetchQueries: [{ query: LIST_TASKS, variables: { ...(bookingId ? { bookingId } : {}), sortColumn, sortDirection } }],
                      });
                    }
                    dispatchMessage(dispatch, String(i18next.t('tasks-saved')));
                    setShowTaskDialog(false);
                    await refetch();
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('save')}
              </Button>
            </Grid>
            <Grid item xs={6} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
              {taskToEdit && taskToEdit.status === ETM_TaskStatus.OPEN && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ marginLeft: 2 }}
                  onClick={async () => {
                    try {
                      await updateTask({
                        variables: {
                          id: taskToEdit.id,
                          input: {
                            description: watch('description'),
                            dueDate: watch('dueDate'),
                            status: ETM_TaskStatus.DONE,
                            categoryId: watch('categoryId') || 0,
                            subjectId: watch('subjectId') || 0,
                            priority: ETM_TaskPriority.NORMAL,
                            assigneeId: watch('assigneeId') || null,
                            hotelId: watch('hotelId') || null,
                            bookingId: watch('bookingId') || null,
                            publicClientId: watch('publicClientId') || null,
                            publicCompanyId: watch('publicCompanyId') || null,
                          },
                        },
                        refetchQueries: [{ query: LIST_TASKS, variables: { ...(bookingId ? { bookingId } : {}), sortColumn, sortDirection } }],
                      });
                      dispatchMessage(dispatch, String(i18next.t('tasks-marked-done')));
                      setShowTaskDialog(false);
                    } catch (err) {
                      dispatchException(dispatch, err);
                    }
                  }}
                >
                  <Check /> {i18next.t('tasks-status-done')}
                </Button>
              )}
              {taskToEdit && taskToEdit.status === ETM_TaskStatus.DONE && (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ marginLeft: 2 }}
                  onClick={async () => {
                    try {
                      await updateTask({
                        variables: {
                          id: taskToEdit.id,
                          input: {
                            description: watch('description'),
                            dueDate: watch('dueDate'),
                            status: ETM_TaskStatus.OPEN,
                            categoryId: watch('categoryId') || 0,
                            subjectId: watch('subjectId') || 0,
                            priority: ETM_TaskPriority.NORMAL,
                            assigneeId: watch('assigneeId') || null,
                            hotelId: watch('hotelId') || null,
                            bookingId: watch('bookingId') || null,
                            publicClientId: watch('publicClientId') || null,
                            publicCompanyId: watch('publicCompanyId') || null,
                          },
                        },
                        refetchQueries: [{ query: LIST_TASKS, variables: { ...(bookingId ? { bookingId } : {}), sortColumn, sortDirection } }],
                      });
                      dispatchMessage(dispatch, String(i18next.t('tasks-reopened')));
                      setShowTaskDialog(false);
                    } catch (err) {
                      dispatchException(dispatch, err);
                    }
                  }}
                >
                  {i18next.t('tasks-mark-undone')}
                </Button>
              )}
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormInputLazyAutocomplete<ListTaskUsersPagedQuery, { search: string | null; skip: number; take: number }>
                  control={control}
                  label={String(i18next.t('tasks-field-assignment'))}
                  name="assigneeId"
                  query={LIST_TASK_USERS_PAGED}
                  getOptions={data => (data?.listTaskUsersPaged?.users || []).map(user => ({ id: user.id, label: user.name }))}
                  getTotalCount={data => data?.listTaskUsersPaged?.totalCount || 0}
                  emptyLabel={String(i18next.t('tasks-empty-none'))}
                  initialOption={
                    taskToEdit?.assignee ? { id: taskToEdit.assignee.id, label: taskToEdit.assignee.name } : null
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputDate control={control} label={String(i18next.t('tasks-field-due-date'))} name="dueDate" required />
              </Grid>
              <Grid item xs={12}>
                <FormInputDropdown
                  enableFilter
                  control={control}
                  disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                  label={String(i18next.t('tasks-field-category'))}
                  name="categoryId"
                  options={(taskCategoriesData?.listTaskCategories || []).map(category => ({
                    value: category.id,
                    label: category.name,
                  }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputDropdown
                  enableFilter
                  control={control}
                  disabled={!watch('categoryId') || (!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy))}
                  label={String(i18next.t('tasks-field-subject'))}
                  name="subjectId"
                  options={(taskSubjectsData?.listTaskSubjects || [])
                    .filter(s => s.categoryId === watch('categoryId'))
                    .map(subject => ({ value: subject.id, label: subject.name }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputText
                  textFieldProps={{ multiline: true, rows: 4 }}
                  control={control}
                  disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                  label={String(i18next.t('tasks-field-description'))}
                  name="description"
                  required
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              {(mode === 'full' || mode === 'company' || mode === 'contact' || mode === 'hotel') && (
                <>
                  <Grid item xs={12}>
                    {mode === 'hotel' ? (
                      <FormInputLazyAutocomplete<ListTaskHotelsPagedQuery, { search: string | null; skip: number; take: number }>
                        control={control}
                        label={String(i18next.t('hotel'))}
                        name="hotelId"
                        query={LIST_TASK_HOTELS_PAGED}
                        getOptions={data =>
                          (data?.listTaskHotelsPaged?.hotels || []).map(hotel => ({ id: hotel.id, label: hotel.name }))
                        }
                        getTotalCount={data => data?.listTaskHotelsPaged?.totalCount || 0}
                        emptyLabel={String(i18next.t('tasks-empty-none'))}
                        disabled
                        initialOption={taskToEdit?.hotel ? { id: taskToEdit.hotel.id, label: taskToEdit.hotel.name } : null}
                      />
                    ) : (
                      <FormInputLazyAutocomplete<ListTaskHotelsPagedQuery, { search: string | null; skip: number; take: number }>
                        control={control}
                        disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                        label={String(i18next.t('hotel'))}
                        name="hotelId"
                        query={LIST_TASK_HOTELS_PAGED}
                        getOptions={data =>
                          (data?.listTaskHotelsPaged?.hotels || []).map(hotel => ({ id: hotel.id, label: hotel.name }))
                        }
                        getTotalCount={data => data?.listTaskHotelsPaged?.totalCount || 0}
                        emptyLabel={String(i18next.t('tasks-empty-none'))}
                        initialOption={taskToEdit?.hotel ? { id: taskToEdit.hotel.id, label: taskToEdit.hotel.name } : null}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputLazyAutocomplete<ListTaskBookingsPagedQuery, { search: string | null; skip: number; take: number }>
                      control={control}
                      disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                      label={String(i18next.t('tasks-field-booking'))}
                      name="bookingId"
                      query={LIST_TASK_BOOKINGS_PAGED}
                      getOptions={data =>
                        (data?.listTaskBookingsPaged?.bookings || []).map(booking => ({
                          id: booking.id,
                          label: booking.title + ' (' + booking.refCode + ')',
                        }))
                      }
                      getTotalCount={data => data?.listTaskBookingsPaged?.totalCount || 0}
                      emptyLabel={String(i18next.t('tasks-empty-none'))}
                      initialOption={
                        taskToEdit?.booking
                          ? {
                              id: taskToEdit.booking.id,
                              label: `${taskToEdit.booking.title} (${taskToEdit.booking.refCode})`,
                            }
                          : null
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {mode === 'company' ? (
                      <FormInputLazyAutocomplete<ListTaskPublicCompaniesPagedQuery, { search: string | null; skip: number; take: number }>
                        control={control}
                        label={String(i18next.t('company'))}
                        name="publicCompanyId"
                        query={LIST_TASK_PUBLIC_COMPANIES_PAGED}
                        getOptions={data =>
                          (data?.listTaskPublicCompaniesPaged?.companies || []).map(company => ({ id: company.id, label: company.name || '' }))
                        }
                        getTotalCount={data => data?.listTaskPublicCompaniesPaged?.totalCount || 0}
                        emptyLabel={String(i18next.t('tasks-empty-none'))}
                        onValueChange={() => setValue('publicClientId', null)}
                        disabled
                        initialOption={
                          taskToEdit?.publicCompany
                            ? { id: taskToEdit.publicCompany.id, label: taskToEdit.publicCompany.name || '' }
                            : null
                        }
                      />
                    ) : watch('publicClientId') && watch('publicCompanyId') ? (
                      <TextField
                        fullWidth
                        disabled
                        label={String(i18next.t('company'))}
                        value={clientCompanyName || ''}
                      />
                    ) : (
                      <FormInputLazyAutocomplete<ListTaskPublicCompaniesPagedQuery, { search: string | null; skip: number; take: number }>
                        control={control}
                        disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                        label={String(i18next.t('company'))}
                        name="publicCompanyId"
                        query={LIST_TASK_PUBLIC_COMPANIES_PAGED}
                        getOptions={data =>
                          (data?.listTaskPublicCompaniesPaged?.companies || []).map(company => ({ id: company.id, label: company.name || '' }))
                        }
                        getTotalCount={data => data?.listTaskPublicCompaniesPaged?.totalCount || 0}
                        emptyLabel={String(i18next.t('tasks-empty-none'))}
                        onValueChange={() => setValue('publicClientId', null)}
                        initialOption={
                          taskToEdit?.publicCompany
                            ? { id: taskToEdit.publicCompany.id, label: taskToEdit.publicCompany.name || '' }
                            : null
                        }
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {watch('bookingId') && bookingData?.viewBooking?.publicClient ? (
                      <TextField
                        fullWidth
                        disabled
                        label={String(i18next.t('contact-person'))}
                        value={`${bookingData.viewBooking.publicClient.firstname} ${bookingData.viewBooking.publicClient.lastname}`}
                      />
                    ) : (
                      <FormInputLazyAutocomplete<
                        ListTaskPublicClientsPagedQuery,
                        { search: string | null; companyId: number | null; skip: number; take: number }
                      >
                        control={control}
                        disabled={!_.isNil(taskToEdit) && _.isNil(taskToEdit?.createdBy)}
                        label={String(i18next.t('contact-person'))}
                        name="publicClientId"
                        query={LIST_TASK_PUBLIC_CLIENTS_PAGED}
                        getOptions={data => {
                          const clients = data?.listTaskPublicClientsPaged?.clients || [];

                          // Only show company names when there are duplicate contact names
                          const nameMap = new Map<string, number>();
                          clients.forEach(client => {
                            const name = `${client.firstname} ${client.lastname}`;
                            nameMap.set(name, (nameMap.get(name) || 0) + 1);
                          });

                          const options = clients.map(client => {
                            const fullName = `${client.firstname} ${client.lastname}`;
                            const isDuplicate = (nameMap.get(fullName) || 0) > 1;

                            return {
                              id: client.id,
                              label: isDuplicate && client.publicCompany?.name
                                ? `${fullName} (${client.publicCompany.name})`
                                : fullName,
                              companyId: client.publicCompany?.id || null,
                              companyName: client.publicCompany?.name || null,
                            };
                          });
                          clientOptionsRef.current = options;
                          return options;
                        }}
                        getTotalCount={data => data?.listTaskPublicClientsPaged?.totalCount || 0}
                        emptyLabel={String(i18next.t('tasks-empty-none'))}
                        additionalVariables={{
                          companyId: watch('publicCompanyId') || null
                        }}
                        onValueChange={(selectedId: any) => {
                          const selectedOption = clientOptionsRef.current.find(o => o.id === selectedId);
                          if (selectedOption?.companyId) {
                            setValue('publicCompanyId', selectedOption.companyId);
                            setClientCompanyName(selectedOption.companyName || null);
                          }
                          if (!selectedId) {
                            setValue('publicCompanyId', null);
                            setClientCompanyName(null);
                          }
                        }}
                        initialOption={
                          taskToEdit?.publicClient
                            ? {
                                id: taskToEdit.publicClient.id,
                                label: `${taskToEdit.publicClient.firstname} ${taskToEdit.publicClient.lastname}`,
                              }
                            : null
                        }
                      />
                    )}
                  </Grid>
                </>
              )}
              {mode === 'booking' && (
                <>
                  <Grid item xs={12} sx={{ display: 'none' }}>
                    <FormInputNumber
                      name="bookingId"
                      control={control}
                      label={String(i18next.t('tasks-field-booking-id'))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      disabled
                      label={String(i18next.t('tasks-field-booking'))}
                      value={props.bookingRefCode ? `${props.bookingTitle} (${props.bookingRefCode})` : String(i18next.t('tasks-current-booking'))}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          {taskToEdit && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <SimpleAccordion header={`${i18next.t('comments')} (${taskToEdit.comments.length})`}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SimpleTable
                      headers={[i18next.t('tasks-comment'), i18next.t('tasks-created-at'), i18next.t('tasks-created-by'), '']}
                      rows={taskToEdit.comments.map(comment => [
                        editingCommentId === comment.id ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            size="small"
                          />
                        ) : (
                          comment.comment
                        ),
                        <FormatDateTime date={comment.createdAt} />,
                        comment.user?.name,
                        editingCommentId === comment.id ? (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <IconButton color="primary"
                              onClick={async () => {
                                try {
                                  await updateTaskComment({
                                    variables: {
                                      id: comment.id,
                                      comment: editingCommentText,
                                    },
                                  });
                                  const r = await refetch();
                                  setTaskToEdit((r.data.listTasks.tasks.find(t => t.id === taskToEdit.id) as TM_TaskOutput) || null);
                                  dispatchMessage(dispatch, String(i18next.t('tasks-comment-updated')));
                                  setEditingCommentId(null);
                                  setEditingCommentText('');
                                } catch (err) {
                                  dispatchException(dispatch, err);
                                }
                              }}>
                              <Save />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditingCommentText('');
                              }}>
                              <Cancel />
                            </IconButton>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <IconButton
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingCommentText(comment.comment);
                              }}>
                              <Edit />
                            </IconButton>
                            <ConfirmationButton
                              icon={true}
                              confirmationQuestion={String(i18next.t('tasks-comment-delete-question'))}
                              confirmationTitle={String(i18next.t('tasks-comment-delete-title'))}
                              onConfirm={async () => {
                                try {
                                  await deleteTaskComment({
                                    variables: {
                                      id: comment.id,
                                    },
                                  });
                                  const r = await refetch();
                                  setTaskToEdit((r.data.listTasks.tasks.find(t => t.id === taskToEdit.id) as TM_TaskOutput) || null);
                                  dispatchMessage(dispatch, String(i18next.t('tasks-comment-deleted')));
                                } catch (err) {
                                  dispatchException(dispatch, err);
                                }
                              }}
                            >
                              <DeleteIcon />
                            </ConfirmationButton>
                          </div>
                        ),
                      ])}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={10}>
                        <FormInputText control={controlComment} label={String(i18next.t('tasks-new-comment'))} name="comment" />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          onClick={async () => {
                            const isValid = await triggerComment();
                            if (!isValid) {
                              return;
                            }
                            try {
                              await addTaskComment({
                                variables: {
                                  taskId: taskToEdit.id,
                                  comment: watchComment('comment'),
                                },
                              });
                              const r = await refetch();
                              setTaskToEdit((r.data.listTasks.tasks.find(t => t.id === taskToEdit.id) as TM_TaskOutput) || null);
                              dispatchMessage(dispatch, String(i18next.t('tasks-comment-saved')));
                              resetComment();
                            } catch (err) {
                              dispatchException(dispatch, err);
                            }
                          }}
                        >
                          <PlusIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </SimpleAccordion>
            </Grid>
          )}
        </Grid>
      </SimpleDialog>
    );
  };

  const tableHeaders = mode === 'full' || mode === 'company' || mode === 'contact' || mode === 'hotel'
    ? [
      <SortableTaskHeader column={ETM_TaskSortColumn.subject} label={String(i18next.t('tasks-table-subject'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.category} label={String(i18next.t('tasks-table-category'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.dueDate} label={String(i18next.t('tasks-table-due-date'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.assignee} label={String(i18next.t('tasks-table-assignee'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.createdBy} label={String(i18next.t('tasks-table-created-by'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.hotel} label={String(i18next.t('tasks-table-hotel'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.publicCompany} label={String(i18next.t('tasks-table-company'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.publicClient} label={String(i18next.t('tasks-table-contact'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.booking} label={String(i18next.t('tasks-table-booking'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
    ]
    : [
      <SortableTaskHeader column={ETM_TaskSortColumn.subject} label={String(i18next.t('tasks-table-subject'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.category} label={String(i18next.t('tasks-table-category'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.dueDate} label={String(i18next.t('tasks-table-due-date'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.assignee} label={String(i18next.t('tasks-table-assignee'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
      <SortableTaskHeader column={ETM_TaskSortColumn.hotel} label={String(i18next.t('tasks-table-hotel'))} currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />,
    ];

  // Tasks are now sorted by the backend
  const tasks = data?.listTasks?.tasks || [];

  const tableRows =
    tasks.map(task => {
      const createdByText = task.createdBy?.name || String(i18next.t('tasks-system'));
      const createdAtText = task.createdAt ? (
        <>
          {createdByText}, <FormatDateTimeShort date={task.createdAt} />
        </>
      ) : createdByText;

      const dueDateCell = moment(task.dueDate).isBefore(new Date()) ? (
        <div style={{ color: '#900', fontWeight: 'bold' }}>
          <FormatDateTimeShort date={task.dueDate} />
        </div>
      ) : (
        <FormatDateTimeShort date={task.dueDate} />
      );

      if (mode === 'booking') {
        if (showHotelColumn) {
          return [
            task.subject?.name,
            task.category?.name,
            dueDateCell,
            task.assignee ? task.assignee?.name : '-',
            task.hotel ? <Link to={`/settings/hotels/${task.hotelId}`}>{task.hotel?.name}</Link> : '',
            createdAtText,
          ];
        }
        return [
          task.subject?.name,
          task.category?.name,
          dueDateCell,
          task.assignee ? task.assignee?.name : '-',
          createdAtText,
        ];
      }

      const baseRow = [
        task.subject?.name,
        task.category?.name,
        dueDateCell,
        task.assignee ? task.assignee?.name : '-',
        createdAtText,
      ];

      if (mode === 'full' || mode === 'company' || mode === 'contact' || mode === 'hotel') {
        return [
          ...baseRow,
          task.hotel ? <Link to={`/settings/hotels/${task.hotelId}`}>{task.hotel?.name}</Link> : '-',
          task.publicCompany ? (
            <Link to={`/clients/companies/${task.publicCompany.id}`}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {task.publicCompany.isPremium ? <Star fontSize="small" style={{ marginRight: 1 }} /> : ''}
                {task.publicCompany?.name}
              </div>
            </Link>
          ) : (
            '-'
          ),
          task.publicClient ? (
            <Link to={`/clients/clients/${task.publicClient.id}`}>
              {task.publicClient?.firstname} {task.publicClient?.lastname}
            </Link>
          ) : (
            '-'
          ),
          task.booking ? (
            <Link to={`/bookings/${task.booking.id}`}>
              {task.booking?.title} ({task.booking?.refCode})
            </Link>
          ) : (
            '-'
          ),
        ];
      }

      return baseRow;
    }) || [];

  return (
    <>
      {_renderTaskDialog()}
      {!hideFilters && _renderFilterDialog()}
      <Grid container spacing={2}>
        {mode === 'booking' && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {i18next.t('tasks-for-booking')}: {props.bookingRefCode}
            </Typography>
          </Grid>
        )}
        <Grid item xs={!hideFilters ? 6 : 12}>
          {!hideCreateButton && (
            <Button
              variant="contained"
              sx={{ marginBottom: 2 }}
              onClick={async () => {
                reset(_createDefaultValues);
                setTaskToEdit(null);
                setEditingCommentId(null);
                setEditingCommentText('');
                setClientCompanyName(null);
                if (mode === 'booking' && bookingId) {
                  setValue('bookingId', bookingId);
                }
                setShowTaskDialog(true);
              }}
            >
              <PlusIcon /> {i18next.t('tasks-create-button')}
            </Button>
          )}
        </Grid>
        {!hideFilters && (
          <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'flex-start' }}>
            <div style={{ width: 250 }}>
              <SimpleDropdown
                value={quickfilterAssignee}
                name="quickfilterAssignee"
                label={String(i18next.t('tasks-table-assignee'))}
                onChange={async value => {
                  setQuickfilterAssignee(value);
                  let assigneeIds: number[] = [];
                  let assigneeEmpty: boolean = false;
                  if (value === 'mine') {
                    assigneeIds = [user?.id!];
                  }
                  if (_.isNumber(value)) {
                    assigneeIds = [value];
                  }
                  if (value === 'all') {
                    assigneeIds = [];
                  }
                  if (value === 'unassigned') {
                    assigneeIds = [];
                    assigneeEmpty = true;
                  }
                  try {
                    await updateTaskFilter({
                      variables: {
                        input: {
                          status: watchFilter('status') as ETM_TaskStatus,
                          assigneeIds: assigneeIds,
                          assigneeEmpty: assigneeEmpty,
                          categoryIds: _.isArray(watchFilter('categoryIds')) ? (watchFilter('categoryIds') as number[]) : [],
                          hotelIds: !_.isNil(watchFilter('hotelId')) ? [watchFilter('hotelId') as number] : [],
                          publicClientIds: !_.isNil(watchFilter('publicClientId')) ? [watchFilter('publicClientId') as number] : [],
                          publicCompanyIds: !_.isNil(watchFilter('publicCompanyId')) ? [watchFilter('publicCompanyId') as number] : [],
                          dueFrom: watchFilter('dueFrom'),
                          dueTo: watchFilter('dueTo'),
                        },
                      },
                      refetchQueries: [{ query: VIEW_TASK_FILTER }],
                    });
                    setFilterVersion(v => v + 1);
                    dispatchMessage(dispatch, String(i18next.t('tasks-filter-saved')));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
                options={[
                  { value: 'mine', label: String(i18next.t('tasks-quickfilter-mine')) },
                  { value: 'all', label: String(i18next.t('tasks-quickfilter-all')) },
                  { value: 'unassigned', label: String(i18next.t('tasks-quickfilter-unassigned')) },
                  { label: '', divider: true },
                  ...(taskUsersData?.listTaskUsers || []).map(user => ({ value: user.id, label: user.name })),
                ]}
              />
            </div>
            <Button
              variant="contained"
              color="secondary"
              sx={{ marginBottom: 2, whiteSpace: 'nowrap' }}
              onClick={async () => {
                setShowFilterDialog(true);
              }}
            >
              <FilterAlt sx={{ mr: 0.5 }} />
              {(() => {
                const filterCount = [
                  ...(_.isArray(watchFilter('categoryIds')) ? (watchFilter('categoryIds') as number[]) : []),
                  ...(watchFilter('hotelId') !== null ? [1] : []),
                  ...(watchFilter('publicClientId') !== null ? [1] : []),
                  ...(watchFilter('publicCompanyId') !== null ? [1] : []),
                  ...(watchFilter('dueFrom') !== null ? [1] : []),
                  ...(watchFilter('dueTo') !== null ? [1] : []),
                ].length;
                return filterCount > 0
                  ? String(i18next.t('tasks-filter-button-with-count', { count: filterCount }))
                  : String(i18next.t('tasks-filter-button'));
              })()}
            </Button>
            {!hideSettings && user?.isAdmin && (
              <Button
                variant="contained"
                color="secondary"
                sx={{ marginBottom: 2 }}
                onClick={async () => {
                  navigate('/tasks/settings');
                }}
              >
                <Settings />
              </Button>
            )}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PagedTable
            filterKey={filterVersion}
            headers={tableHeaders}
            rows={tableRows}
            totalCount={data?.listTasks?.totalCount}
            refetchPage={(skip, take) => {
              refetch({ bookingId, skip, take, sortColumn, sortDirection });
            }}
            onRowClick={index => {
              const task = tasks[index];
              if (task) {
                setTaskToEdit(task as TM_TaskOutput);
                reset(task as any);
                setEditingCommentId(null);
                setEditingCommentText('');
                setClientCompanyName(task.publicCompany?.name ?? null);
                setShowTaskDialog(true);
              }
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}
